import { useEffect, useState, useMemo } from 'react'
import { toast } from '../lib/toast'
import { UserRole } from '../enums/userRole'

/**
 * Hook to handle account blocking with countdown timer
 * Parses block time from error messages and manages countdown state
 */
export function useAccountBlock() {
  const [blockedUntil, setBlockedUntil] = useState<number | null>(null)
  const [timeRemaining, setTimeRemaining] = useState<string>('')
  const [isBlockedState, setIsBlockedState] = useState<boolean>(false)
  // Parse time from error message and calculate block end time
  const parseBlockTime = (errorMessage: string): number | null => {
    // Match patterns like "X minute(s) and Y second(s)" or "Y second(s)"
    const minuteMatch = errorMessage.match(/(\d+)\s+minute/i)
    const secondMatch = errorMessage.match(/(\d+)\s+second/i)

    if (!minuteMatch && !secondMatch) {
      return null
    }

    const minutes = minuteMatch ? parseInt(minuteMatch[1], 10) : 0
    const seconds = secondMatch ? parseInt(secondMatch[1], 10) : 0
    const totalSeconds = minutes * 60 + seconds

    // Return timestamp when block will end
    return Date.now() + (totalSeconds * 1000)
  }

  // Format time remaining for display
  const formatTimeRemaining = (ms: number): string => {
    const totalSeconds = Math.ceil(ms / 1000)
    const minutes = Math.floor(totalSeconds / 60)
    const seconds = totalSeconds % 60

    if (minutes > 0) {
      return `${minutes}:${seconds.toString().padStart(2, '0')}`
    }
    return `${seconds}s`
  }

  // Check if account is currently blocked
  const isBlocked = (): boolean => {
    return blockedUntil !== null && Date.now() < blockedUntil
  }

  // Handle error and check if it contains block information
  const handleError = (error: any): void => {
    const errorMessage = error?.message || ''

    // Check for permanent block (userRole: 5)
    if (error.userRole === UserRole.Blocked) {
      setIsBlockedState(true)
      // Don't set blockedUntil for permanent blocks
      return
    }

    // Check for temporary block with blockTime property
    if (error.blockTime) {
      setBlockedUntil(error.blockTime)
      setIsBlockedState(false)
      return
    }

    // Check if error message contains temporary block time
    const blockEndTime = parseBlockTime(errorMessage)
    if (blockEndTime) {
      setBlockedUntil(blockEndTime)
      setIsBlockedState(false)
      return
    }

    // Clear block if it's a different error (not a block-related error)
    setBlockedUntil(null)
    setIsBlockedState(false)
  }

  // Clear block state (e.g., on successful login/register)
  const clearBlock = (): void => {
    setBlockedUntil(null)
    setIsBlockedState(false)
  }

  // Check if blocked before submitting (shows error if blocked)
  const checkBeforeSubmit = (): boolean => {
    if (isBlocked() && blockedUntil) {
      const remaining = blockedUntil - Date.now()
      toast.error(`Account is temporarily blocked. Please try again in ${formatTimeRemaining(remaining)}.`)
      return false
    }
    return true
  }

  // Update countdown timer
  useEffect(() => {
    if (!blockedUntil) {
      setTimeRemaining('')
      return
    }

    const updateTimer = () => {
      const now = Date.now()
      const remaining = blockedUntil - now

      if (remaining <= 0) {
        setBlockedUntil(null)
        setTimeRemaining('')
        return
      }

      setTimeRemaining(formatTimeRemaining(remaining))
    }

    updateTimer()
    const interval = setInterval(updateTimer, 1000)

    return () => clearInterval(interval)
  }, [blockedUntil])

  // Compute isBlocked reactively based on blockedUntil
  const blocked = useMemo(() => {
    return blockedUntil !== null && Date.now() < blockedUntil
    || isBlockedState
  }, [blockedUntil, timeRemaining, isBlockedState]) // Re-compute when blockedUntil, timeRemaining, or isBlockedState changes

  return {
    blockedUntil,
    timeRemaining,
    isBlocked: blocked,
    handleError,
    clearBlock,
    checkBeforeSubmit,
    formatTimeRemaining,
  }
}
